/**
 * Sistema permessi — singola sorgente di verità.
 * Quando aggiungi una pagina alla sidebar, aggiungila qui.
 */

export type Ability = "view" | "create" | "edit" | "delete";

export interface SpecialAction {
  key: string;
  label: string;
}

export interface PermissionPage {
  key: string;
  label: string;
  href: string;
  abilities: Ability[];
  actions?: SpecialAction[];
}

export interface PermissionGroup {
  group: string;
  pages: PermissionPage[];
}

export const PERMISSION_SECTIONS: PermissionGroup[] = [
  {
    group: "Principale",
    pages: [
      { key: "dashboard", label: "Dashboard", href: "/", abilities: ["view"] },
      { key: "clienti", label: "Clienti", href: "/clienti", abilities: ["view", "create", "edit", "delete"], actions: [
        { key: "sendMessage", label: "Invia messaggi" },
        { key: "sendInvoice", label: "Invia fattura" },
      ]},
      { key: "contratti", label: "Contratti", href: "/contratti", abilities: ["view", "create", "edit", "delete"] },
      { key: "prodotti", label: "Prodotti", href: "/prodotti", abilities: ["view", "create", "edit", "delete"] },
    ],
  },
  {
    group: "Contabilità",
    pages: [
      { key: "contabilita_hub", label: "Cruscotto Contabilità", href: "/contabilita", abilities: ["view"] },
      { key: "fatture", label: "Fatture", href: "/contabilita/fatture", abilities: ["view", "create", "edit", "delete"], actions: [
        { key: "sendSDI", label: "Invia a SDI" },
        { key: "creditNote", label: "Genera nota di credito" },
        { key: "print", label: "Stampa/Scarica PDF" },
      ]},
      { key: "pagamenti", label: "Pagamenti", href: "/contabilita/pagamenti", abilities: ["view", "create"], actions: [
        { key: "registerManual", label: "Registra pagamento manuale" },
      ]},
      { key: "sepa", label: "Ordini SEPA", href: "/contabilita/sepa", abilities: ["view", "create", "edit", "delete"], actions: [
        { key: "generate", label: "Genera ordine" },
        { key: "loadBank", label: "Carica in banca" },
      ]},
      { key: "cbi_import", label: "Riconciliazione CBI", href: "/contabilita/cbi-import", abilities: ["view"], actions: [
        { key: "confirm", label: "Conferma riconciliazione" },
      ]},
      { key: "insoluti", label: "Insoluti", href: "/contabilita/insoluti", abilities: ["view", "create", "edit"], actions: [
        { key: "import", label: "Importa da banca" },
        { key: "notify", label: "Invia notifiche" },
        { key: "resolve", label: "Risolvi insoluto" },
      ]},
      { key: "nexi", label: "Pagamenti Nexi", href: "/contabilita/nexi", abilities: ["view"] },
      { key: "sella", label: "Pagamenti Sella", href: "/contabilita/sella", abilities: ["view"] },
      { key: "revolut", label: "Revolut", href: "/contabilita/revolut", abilities: ["view"] },
      { key: "tokenizzazione", label: "Tokenizzazione", href: "/contabilita/tokenizzazione", abilities: ["view", "create"] },
      { key: "fornitori", label: "Fatture Fornitori", href: "/contabilita/fornitori", abilities: ["view", "create", "edit"], actions: [
        { key: "importPEC", label: "Importa da PEC" },
        { key: "register", label: "Registra nello scadenziario" },
      ]},
      { key: "contabilita_interna", label: "Contabilità Interna", href: "/contabilita/interna", abilities: ["view", "create", "edit", "delete"] },
      { key: "spese_dirette", label: "Spese Dirette", href: "/contabilita/spese-dirette", abilities: ["view", "create", "edit", "delete"] },
      { key: "giroconti", label: "Giroconti", href: "/contabilita/giroconti", abilities: ["view", "create", "delete"] },
      { key: "scadenze", label: "Gestione Scadenze", href: "/contabilita/scadenze", abilities: ["view", "create", "edit", "delete"], actions: [
        { key: "pay", label: "Registra pagamenti" },
        { key: "compensazione", label: "Compensazione" },
      ]},
      { key: "conti_bancari", label: "Conti Bancari", href: "/contabilita/conti-bancari", abilities: ["view", "create", "edit", "delete"] },
      { key: "assegni", label: "Assegni", href: "/contabilita/assegni", abilities: ["view", "create", "edit", "delete"] },
      { key: "personale", label: "Personale", href: "/contabilita/personale", abilities: ["view", "create", "edit", "delete"] },
    ],
  },
  {
    group: "Configurazione",
    pages: [
      { key: "imposte", label: "Imposte", href: "/configurazione/imposte", abilities: ["view", "create", "edit", "delete"] },
      { key: "metodi_pagamento", label: "Metodi Pagamento", href: "/configurazione/metodi-pagamento", abilities: ["view", "create", "edit", "delete"] },
      { key: "termini_pagamento", label: "Termini Pagamento", href: "/configurazione/termini-pagamento", abilities: ["view", "create", "edit", "delete"] },
      { key: "tipi_documento", label: "Tipi Documento", href: "/configurazione/tipi-documento", abilities: ["view", "create", "edit", "delete"] },
      { key: "pacchetti_ricarica", label: "Piani Assistenza", href: "/configurazione/pacchetti-ricarica", abilities: ["view", "create", "edit", "delete"] },
      { key: "configurazione_reseller", label: "Gestione Reseller", href: "/configurazione/reseller", abilities: ["view", "create", "edit", "delete"] },
    ],
  },
  {
    group: "Strumenti",
    pages: [
      { key: "appunti", label: "Appunti", href: "/appunti", abilities: ["view", "create", "edit", "delete"] },
      { key: "comunicazioni", label: "Comunicazioni", href: "/comunicazioni", abilities: ["view", "create"] },
      { key: "calendario", label: "Calendario", href: "/calendario", abilities: ["view", "create", "edit"] },
      { key: "impostazioni", label: "Impostazioni", href: "/impostazioni", abilities: ["view", "edit"] },
      { key: "utenti", label: "Gestione Utenti", href: "/impostazioni/utenti", abilities: ["view", "create", "edit", "delete"] },
      { key: "istanze_gestite", label: "Istanze Gestite", href: "/admin/istanze", abilities: ["view", "edit"], actions: [
        { key: "toggleModule", label: "Attiva/Disattiva modulo remoto" },
      ]},
    ],
  },
  {
    group: "Dispositivi & Assistenze",
    pages: [
      { key: "rt_dispositivi", label: "Registro Matricole", href: "/dispositivi", abilities: ["view", "create", "edit"], actions: [
        { key: "assign", label: "Assegna/rientro dispositivo" },
        { key: "verification", label: "Registra verificazione" },
      ]},
      { key: "rt_scadenzario", label: "Scadenzario Assistenze", href: "/assistenze/scadenzario", abilities: ["view"], actions: [
        { key: "renewInvoice", label: "Genera fattura rinnovo" },
        { key: "confirmRenewal", label: "Conferma proroga contratto" },
        { key: "scheduleVerification", label: "Pianifica verificazione" },
      ]},
    ],
  },
  {
    group: "Vendita Al Banco",
    pages: [
      { key: "vab_dashboard", label: "Dashboard VAB", href: "/vendita-al-banco", abilities: ["view"] },
      { key: "vab_pos", label: "Nuova Vendita (POS)", href: "/vendita-al-banco/pos", abilities: ["view", "create"], actions: [
        { key: "freeRow", label: "Aggiungi riga libera" },
        { key: "applyDiscount", label: "Applica sconto per riga" },
        { key: "applyWithholding", label: "Applica ritenuta d'acconto" },
        { key: "sendSdi", label: "Invia fattura a SDI" },
        { key: "sendEmail", label: "Invia documento per email" },
      ]},
      { key: "cassa", label: "Cassa Scontrini", href: "/cassa", abilities: ["view", "create"], actions: [
        { key: "void", label: "Annulla scontrino" },
        { key: "refund", label: "Reso scontrino" },
      ]},
      { key: "vab_magazzino", label: "Magazzino", href: "/vendita-al-banco/magazzino", abilities: ["view", "create", "edit", "delete"], actions: [
        { key: "manageCategories", label: "Gestisci categorie" },
        { key: "stockAdjust", label: "Carico/Scarico/Rettifica" },
        { key: "viewHistory", label: "Visualizza storico movimenti" },
      ]},
      { key: "vab_vendite", label: "Storico Vendite", href: "/vendita-al-banco/vendite", abilities: ["view", "edit", "delete"], actions: [
        { key: "cancel", label: "Annulla vendita" },
        { key: "print", label: "Stampa PDF" },
        { key: "sendEmail", label: "Invia per email" },
        { key: "changePayment", label: "Cambia metodo pagamento" },
      ]},
      { key: "vab_chiusura", label: "Chiusura Serale", href: "/vendita-al-banco/chiusura-serale", abilities: ["view", "create"], actions: [
        { key: "reopen", label: "Riapri chiusura confermata" },
        { key: "print", label: "Stampa PDF chiusura" },
      ]},
      { key: "vab_assistenza", label: "Assistenza Tecnica", href: "/vendita-al-banco/assistenza", abilities: ["view", "create", "edit", "delete"], actions: [
        { key: "print", label: "Stampa scheda PDF" },
        { key: "sendEmail", label: "Invia per email" },
        { key: "sendWhatsApp", label: "Invia su WhatsApp" },
      ]},
    ],
  },
];

// Mappa href → permission key per il middleware
export const HREF_TO_PERMISSION: Record<string, string> = {};
for (const group of PERMISSION_SECTIONS) {
  for (const page of group.pages) {
    HREF_TO_PERMISSION[page.href] = page.key;
  }
}

// Scope per permessi a livello riga:
//  - "ALL" (default se mancante): accede a tutti i record
//  - "OWN": accede solo ai record "propri" (es. WorkOrder con leadTechnicianId = user.id)
export type PermissionScope = "ALL" | "OWN";

// Pagine che supportano lo scope OWN. Le altre ignorano il campo.
export const SCOPED_PAGES = new Set<string>(["calendario"]);

// Tipi per i permessi salvati nel JSON
export type PagePermissions = {
  view?: boolean;
  create?: boolean;
  edit?: boolean;
  delete?: boolean;
  actions?: Record<string, boolean>;
  /** Solo per pagine in SCOPED_PAGES. Se omesso → "ALL". */
  scope?: PermissionScope;
};

export type RolePermissions = Record<string, PagePermissions | boolean | undefined> & {
  _all?: boolean; // Admin: tutto abilitato
};

/**
 * Verifica se un set di permessi consente un'abilità su una pagina.
 */
export function hasPermission(
  permissions: RolePermissions | null | undefined,
  pageKey: string,
  ability: Ability | string,
): boolean {
  if (!permissions) return false;
  if (permissions._all) return true;

  const page = permissions[pageKey];
  if (!page || typeof page === "boolean") return false;

  const p = page as PagePermissions;

  // Abilità standard (view, create, edit, delete)
  if (ability === "view" || ability === "create" || ability === "edit" || ability === "delete") {
    return p[ability] === true;
  }

  // Azione speciale (es. "actions.sendSDI")
  if (ability.startsWith("actions.")) {
    const actionKey = ability.replace("actions.", "");
    return p.actions?.[actionKey] === true;
  }

  // Azione speciale senza prefisso
  return p.actions?.[ability] === true;
}

/**
 * Genera permessi "tutto abilitato" per admin.
 */
export function generateFullPermissions(): RolePermissions {
  const perms: RolePermissions = { _all: true };
  return perms;
}

/**
 * Genera permessi "solo lettura" — view su tutto, nient'altro.
 */
export function generateReadOnlyPermissions(): RolePermissions {
  const perms: RolePermissions = {};
  for (const group of PERMISSION_SECTIONS) {
    for (const page of group.pages) {
      perms[page.key] = { view: true };
    }
  }
  return perms;
}

/**
 * Genera permessi vuoti (nessun accesso).
 */
export function generateEmptyPermissions(): RolePermissions {
  const perms: RolePermissions = {};
  for (const group of PERMISSION_SECTIONS) {
    for (const page of group.pages) {
      perms[page.key] = { view: false };
    }
  }
  return perms;
}

/**
 * Ritorna lo scope effettivo per una pagina. Default "ALL" se non specificato
 * o se la pagina non supporta scoping (SCOPED_PAGES).
 */
export function getPermissionScope(
  permissions: RolePermissions | null | undefined,
  pageKey: string,
): PermissionScope {
  if (!permissions) return "ALL";
  if (permissions._all) return "ALL";
  if (!SCOPED_PAGES.has(pageKey)) return "ALL";
  const page = permissions[pageKey];
  if (!page || typeof page === "boolean") return "ALL";
  return (page as PagePermissions).scope === "OWN" ? "OWN" : "ALL";
}

/**
 * Preset "Tecnico Installatore" — solo campo (WO assegnati a lui),
 * può creare/gestire account RADIUS, mandare messaggi al cliente,
 * niente contabilità/configurazione/admin.
 */
export function generateTechnicianPermissions(): RolePermissions {
  return {
    // Principale
    dashboard: { view: true },
    clienti: { view: true, edit: true, actions: { sendMessage: true } },
    contratti: { view: true },
    prodotti: { view: true },
    // Contabilità — tutto NO
    fatture: { view: false },
    pagamenti: { view: false },
    sepa: { view: false },
    insoluti: { view: false },
    nexi: { view: false },
    tokenizzazione: { view: false },
    fornitori: { view: false },
    contabilita_interna: { view: false },
    scadenze: { view: false },
    conti_bancari: { view: false },
    // Configurazione — tutto NO
    imposte: { view: false },
    metodi_pagamento: { view: false },
    termini_pagamento: { view: false },
    tipi_documento: { view: false },
    pacchetti_ricarica: { view: false },
    configurazione_reseller: { view: false },
    // Strumenti
    appunti: { view: true, create: true, edit: true, delete: true },
    comunicazioni: { view: true, create: true },
    // Chiave: calendario con scope OWN
    calendario: { view: true, create: true, edit: true, scope: "OWN" },
    impostazioni: { view: false },
    utenti: { view: false },
    istanze_gestite: { view: false },
    // Vendita Al Banco — fuori scope tecnico
    vab_dashboard: { view: false },
    vab_pos: { view: false },
    vab_magazzino: { view: false },
    vab_vendite: { view: false },
    vab_chiusura: { view: false },
    vab_assistenza: { view: false },
  };
}
